import React, { useRef, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Page from "../components/Page";
import Select from "react-select";
import {
  IconPlus,
  IconTrash,
  IconCalendar,
  IconUser,
  IconShoppingBag,
  IconReceipt,
  IconCreditCard,
  IconHistory,
  IconFilter,
  IconX,
  IconArrowRight,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useSuppliers, usePurchases, createPurchase, useInventoryItems, getPurchaseInvoiceById } from "../controllers/inventory.controller";
import { usePaymentTypes } from "../controllers/settings.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import { toast } from "react-hot-toast";
import { useTheme } from "../contexts/ThemeContext";
import ApiClient from "../helpers/ApiClient";
import { setDetailsForPurchasePrint } from "../helpers/PurchaseReceiptHelper";
import { CURRENCIES } from "../config/currencies.config";

const SafeIconPrinter = ({ size = 24, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);


const getDateRange = (filterKey, customFrom = null, customTo = null) => {
  const now = new Date();
  const getFormattedDate = (date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  const todayStr = getFormattedDate(now);

  switch (filterKey) {
    case "all":
      return { from: null, to: null };
    case "today":
      return { from: todayStr, to: todayStr };
    case "yesterday": {
      const yesterday = new Date();
      yesterday.setDate(now.getDate() - 1);
      const yesterdayStr = getFormattedDate(yesterday);
      return { from: yesterdayStr, to: yesterdayStr };
    }
    case "tomorrow": {
      const tomorrow = new Date();
      tomorrow.setDate(now.getDate() + 1);
      const tomorrowStr = getFormattedDate(tomorrow);
      return { from: tomorrowStr, to: tomorrowStr };
    }
    case "last_7days": {
      const last7 = new Date();
      last7.setDate(now.getDate() - 7);
      return { from: getFormattedDate(last7), to: todayStr };
    }
    case "this_month": {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: getFormattedDate(firstDay), to: getFormattedDate(lastDay) };
    }
    case "last_month": {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: getFormattedDate(firstDay), to: getFormattedDate(lastDay) };
    }
    case "custom":
      return { from: customFrom, to: customTo };
    default:
      return { from: null, to: null };
  }
};

export default function PurchasesPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const filters = [
    { key: "all", value: "All Purchases" },
    { key: "this_month", value: t("invoices.this_month") },
    { key: "today", value: t("invoices.today") },
    { key: "yesterday", value: t("invoices.yesterday") },
    { key: "last_7days", value: t("invoices.last_7days") },
    { key: "last_month", value: t("invoices.last_month") },
    { key: "custom", value: t("invoices.custom") },
  ];

  const filterTypeRef = useRef();
  const fromDateRef = useRef();
  const toDateRef = useRef();

  const now = new Date();
  const defaultDateFrom = `${now.getFullYear()}-${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
  const defaultDateTo = `${now.getFullYear()}-${(now.getMonth() + 2)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;

  const [filter, setFilter] = useState("today");
  const [fromDate, setFromDate] = useState(defaultDateFrom);
  const [toDate, setToDate] = useState(defaultDateTo);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [items, setItems] = useState([{ inventory_item_id: "", quantity: "", total_price: "", unit_price: 0, purchase_unit_id: "", conversion_factor: 1, purchase_unit: "" }]);
  const [supplierId, setSupplierId] = useState("");
  const [purchaseInvoiceNo, setPurchaseInvoiceNo] = useState("");
  const [unitConversions, setUnitConversions] = useState([]);

  useEffect(() => {
    ApiClient.get('/settings/unit-conversions').then(res => setUnitConversions(res.data)).catch(console.error);
  }, []);
  const [purchaseDate, setPurchaseDate] = useState(defaultDateFrom);
  const [paidAmount, setPaidAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [notes, setNotes] = useState("");

  const { data: suppliers } = useSuppliers();
  const { data: inventoryItems } = useInventoryItems({ status: "all" });
  const { data: paymentTypes } = usePaymentTypes({ activeOnly: true });

  const { data: storeSettings } = useStoreSettings();
  const computedDateRange = getDateRange(filter, fromDate, toDate);
  const { data: purchases, mutate: mutatePurchases } = usePurchases({
    from: computedDateRange.from,
    to: computedDateRange.to
  });

  const [searchQuery, setSearchQuery] = useState("");
  const filteredPurchases = useMemo(() => {
    if (!purchases || !Array.isArray(purchases)) return [];
    if (!searchQuery.trim()) return purchases;
    const q = searchQuery.toLowerCase().trim();
    return purchases.filter((p) => {
      const invNo = (p.purchase_invoice_no || "").toLowerCase();
      const supName = (p.supplier_name || "").toLowerCase();
      const notes = (p.notes || "").toLowerCase();
      const pId = String(p.id);
      return invNo.includes(q) || supName.includes(q) || notes.includes(q) || pId.includes(q);
    });
  }, [purchases, searchQuery]);

  const totalPages = Math.max(1, Math.ceil((filteredPurchases?.length || 0) / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedPurchases = useMemo(() => {
    return filteredPurchases.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);
  }, [filteredPurchases, safeCurrentPage, pageSize]);

  const inventoryItemOptions = useMemo(() => {
    return inventoryItems?.items?.map(i => ({ value: i.id, label: `${i.title} (${i.unit})` })) || [];
  }, [inventoryItems]);

  const calculateSubtotal = () => {
    return items.reduce((acc, item) => acc + (parseFloat(String(item.total_price).replace(/,/g, "")) || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - (parseFloat(String(discountAmount).replace(/,/g, "")) || 0);
  };

  const supplierRef = useRef(null);
  const purchaseInvoiceNoRef = useRef(null);
  const purchaseDateRef = useRef(null);
  const itemSelectRefs = useRef([]);
  const unitRefs = useRef([]);
  const quantityRefs = useRef([]);
  const totalPriceRefs = useRef([]);
  const discountRef = useRef(null);
  const paidAmountRef = useRef(null);
  const paymentMethodRef = useRef(null);
  const notesRef = useRef(null);
  const submitBtnRef = useRef(null);

  const openAddPurchaseModal = () => {
    document.getElementById("modal-add-purchase").showModal();
    setTimeout(() => {
      supplierRef.current?.focus();
    }, 100);
  };

  const addItemRow = () => {
    setItems((prev) => {
      const next = [
        ...prev,
        {
          inventory_item_id: "",
          quantity: "",
          total_price: "",
          unit_price: 0,
          purchase_unit_id: "",
          conversion_factor: 1,
          purchase_unit: "",
        },
      ];
      setTimeout(() => {
        itemSelectRefs.current[next.length - 1]?.focus();
      }, 80);
      return next;
    });
  };

  const removeItemRow = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-update conversion factor if purchase_unit changes
    if (field === 'purchase_unit_id') {
       const selectedMapping = unitConversions.find(c => String(c.purchase_unit_id) === String(value) && String(c.inventory_item_id) === String(newItems[index].inventory_item_id));
       if (selectedMapping) {
           newItems[index].conversion_factor = selectedMapping.conversion_factor;
           newItems[index].purchase_unit = selectedMapping.purchase_unit_name;
       } else {
           newItems[index].conversion_factor = 1;
           newItems[index].purchase_unit = "";
       }
    }

    // Auto calculate unit price when qty or total_price changes
    if (field === 'quantity' || field === 'total_price' || field === 'purchase_unit_id') {
        const qty = parseFloat(String(newItems[index].quantity).replace(/,/g, "")) || 0;
        const total = parseFloat(String(newItems[index].total_price).replace(/,/g, "")) || 0;
        if (qty > 0) {
            newItems[index].unit_price = total / qty;
        } else {
            newItems[index].unit_price = 0;
        }
    }
    
    setItems(newItems);
  };

  const printPurchaseInvoice = async (purchase) => {
    try {
      toast.loading("Loading invoice details...");
      const fullPurchase = await getPurchaseInvoiceById(purchase.id);
      
      const initRes = await ApiClient.get("/orders/init");
      const storeSettings = initRes.data?.storeSettings || {};
      const printSettings = initRes.data?.printSettings || {};
      
      const currencySymbol = CURRENCIES.find(c => c.cc === storeSettings.currency)?.symbol || storeSettings.currency || "";

      toast.dismiss();

      setDetailsForPurchasePrint({
        purchase: fullPurchase,
        storeSettings,
        printSettings,
        currency: currencySymbol
      });

      const printWindow = window.open("/print-purchase", "_blank", "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=800,height=800");
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 400);
        };
      }
    } catch (error) {
      toast.dismiss();
      toast.error("Error loading print details.");
      console.error(error);
    }
  };

  const btnCreatePurchase = async () => {
    if (!supplierId || items.some(i => !i.inventory_item_id)) {
      toast.error("Please select supplier and all items!");
      return;
    }

    const sub_total = calculateSubtotal();
    const total = calculateTotal();

    try {
      toast.loading("Recording purchase...");
      const res = await createPurchase({
        supplier_id: supplierId,
        purchase_date: purchaseDate,
        items: items.map(i => ({
          ...i,
          total_price: parseFloat(String(i.total_price).replace(/,/g, "")) || 0
        })),
        total_amount: calculateSubtotal(),
        discount_amount: parseFloat(String(discountAmount).replace(/,/g, "")) || 0,
        paid_amount: parseFloat(String(paidAmount).replace(/,/g, "")) || 0,
        payment_method: paymentMethod,
        notes,
        purchase_invoice_no: purchaseInvoiceNo
      });

      if (res.status === 201 || res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message || "Purchase recorded successfully");
        mutatePurchases();
        document.getElementById("modal-add-purchase").close();
        // reset form
        setItems([{ inventory_item_id: "", quantity: 1, unit_price: 0 }]);
        setSupplierId("");
        setPurchaseInvoiceNo("");
        setDiscountAmount(0);
        setPaidAmount(0);
        setNotes("");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error recording purchase");
    }
  };

  return (
    <Page>
      <div className="flex flex-wrap gap-4 flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold">Purchases & Inventory Inflow</h3>
          <p className="text-sm text-gray-500 mt-1">
            Showing Purchases for {filters.find(f => f.key === filter)?.value || filter}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search invoice, supplier..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="input input-sm input-bordered rounded-lg w-44 md:w-56"
          />
          <button
            onClick={() => document.getElementById("filter-dialog").showModal()}
            className="w-8 h-8 flex items-center justify-center text-gray-400 rounded-full active:scale-95 transition hover:bg-restro-button-hover"
            title="Filter Purchases"
          >
            <IconFilter />
          </button>
          <Link
            to="/dashboard/inventory/accounts-payable"
            className="btn btn-sm btn-outline border-gray-300 dark:border-gray-700 flex items-center gap-1 font-bold"
            title="Accounts Payable & FIFO Dues Settlement"
          >
            <IconReceipt size={16} /> Payables / Dues
          </Link>
          <button onClick={openAddPurchaseModal} className="btn btn-primary btn-sm flex items-center gap-1">
            <IconPlus size={18} /> New Purchase
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl">
        <table className="table w-full">
          <thead>
            <tr className="bg-restro-bg-gray">
              <th>ID</th>
              <th>Invoice No</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Subtotal</th>
              <th>Discount</th>
              <th>Total</th>
              <th>Paid</th>
              <th>Due</th>
              <th>Status</th>
              <th>Notes</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(!paginatedPurchases || paginatedPurchases.length === 0) && (
              <tr>
                <td colSpan="12" className="text-center py-8 text-gray-500">
                  {purchases && purchases.length > 0 && searchQuery
                    ? "No purchases match your search."
                    : "No purchases found for the selected period."}
                </td>
              </tr>
            )}
            {paginatedPurchases && Array.isArray(paginatedPurchases) && paginatedPurchases.map((p) => (
              <tr key={p.id}>
                <td>#{p.id}</td>
                <td>{p.purchase_invoice_no || "-"}</td>
                <td>{new Date(p.purchase_date).toLocaleDateString()}</td>
                <td>{p.supplier_name}</td>
                <td className="font-bold">{(Number(String(p.total_amount).replace(/,/g, "")) || 0).toFixed(2)}</td>
                <td className="text-blue-500">{(Number(String(p.discount_amount).replace(/,/g, "")) || 0).toFixed(2)}</td>
                <td className="font-bold">{((Number(String(p.total_amount).replace(/,/g, "")) || 0) - (Number(String(p.discount_amount).replace(/,/g, "")) || 0)).toFixed(2)}</td>
                <td className="text-green-500">{(Number(String(p.paid_amount).replace(/,/g, "")) || 0).toFixed(2)}</td>
                <td className={(Number(String(p.due_amount).replace(/,/g, "")) || 0) > 0 ? "text-red-500 font-bold" : ""}>{(Number(String(p.due_amount).replace(/,/g, "")) || 0).toFixed(2)}</td>
                <td>
                  <div className={`badge ${p.payment_status === 'paid' ? 'badge-success' : p.payment_status === 'partial' ? 'badge-warning' : 'badge-error'}`}>
                    {p.payment_status}
                  </div>
                </td>
                <td className="text-xs">{p.notes}</td>
                <td className="text-center">
                  <button onClick={() => printPurchaseInvoice(p)} className="btn btn-sm btn-ghost btn-circle" title="Print Purchase Invoice">
                    <SafeIconPrinter size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination controls */}
        {filteredPurchases?.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/20">
            <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
              <span>
                Showing {((safeCurrentPage - 1) * pageSize) + 1} to {Math.min(safeCurrentPage * pageSize, filteredPurchases.length)} of {filteredPurchases.length} purchases
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div className="join">
              <button
                className="join-item btn btn-xs"
                disabled={safeCurrentPage === 1}
                onClick={() => setCurrentPage(1)}
                title="First Page"
              >
                <IconChevronsLeft size={14} />
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={safeCurrentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                title="Previous Page"
              >
                <IconChevronLeft size={14} />
              </button>
              <button className="join-item btn btn-xs no-animation font-semibold">
                Page {safeCurrentPage} of {totalPages}
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                title="Next Page"
              >
                <IconChevronRight size={14} />
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
                title="Last Page"
              >
                <IconChevronsRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <dialog id="modal-add-purchase" className="modal">
        <div className="modal-box max-w-4xl border border-restro-border-green dark:rounded-2xl">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><IconShoppingBag /> New Purchase Record</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Supplier *</label>
              <select
                ref={supplierRef}
                value={supplierId}
                onChange={(e) => {
                  setSupplierId(e.target.value);
                  if (e.target.value) {
                    setTimeout(() => {
                      purchaseInvoiceNoRef.current?.focus();
                      purchaseInvoiceNoRef.current?.select();
                    }, 50);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    purchaseInvoiceNoRef.current?.focus();
                    purchaseInvoiceNoRef.current?.select();
                  }
                }}
                className="select select-bordered w-full"
              >
                <option value="">Select Supplier</option>
                {(Array.isArray(suppliers) ? suppliers : (suppliers?.suppliers || [])).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Purchase Invoice Number</label>
              <input
                ref={purchaseInvoiceNoRef}
                type="text"
                value={purchaseInvoiceNo}
                onChange={(e) => setPurchaseInvoiceNo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    purchaseDateRef.current?.focus();
                  }
                }}
                className="input input-bordered w-full"
                placeholder="e.g. INV-1234"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Purchase Date *</label>
              <input
                ref={purchaseDateRef}
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    itemSelectRefs.current[0]?.focus();
                  }
                }}
                className="input input-bordered w-full"
              />
            </div>
          </div>

          <div className="mb-4">
            <h4 className="font-bold mb-2 flex items-center gap-2 text-sm"><IconReceipt size={16} /> Purchase Items</h4>
            <div className="space-y-4">
              {items.map((item, index) => {
                const selectedInventoryItem = inventoryItems?.items?.find(i => String(i.id) === String(item.inventory_item_id));
                const invUnit = selectedInventoryItem?.unit || "";
                
                // Calculate dynamic preview
                const qty = parseFloat(item.quantity) || 0;
                const total = parseFloat(item.total_price) || 0;
                const factor = parseFloat(item.conversion_factor) || 1;
                const totalBaseUnits = qty * factor;
                const pricePerBaseUnit = totalBaseUnits > 0 ? (total / totalBaseUnits) : 0;
                const currency = CURRENCIES.find(c => c.cc === storeSettings?.currency)?.symbol || storeSettings?.currency || "";

                return (
                <div key={index} className="flex flex-col gap-2 p-3 bg-restro-gray rounded-xl border border-restro-border-green">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 font-medium mb-1 block">Inventory Item</label>
                      <Select
                        ref={(el) => (itemSelectRefs.current[index] = el)}
                        className="text-sm"
                        options={inventoryItemOptions}
                        value={item.inventory_item_id ? { value: item.inventory_item_id, label: `${selectedInventoryItem?.title} (${invUnit})` } : null}
                        onChange={(selected) => {
                          updateItem(index, 'inventory_item_id', selected ? selected.value : "");
                          if (selected) {
                            const hasConversions = unitConversions.some(c => String(c.inventory_item_id) === String(selected.value));
                            setTimeout(() => {
                              if (hasConversions) {
                                unitRefs.current[index]?.focus();
                              } else {
                                quantityRefs.current[index]?.focus();
                                quantityRefs.current[index]?.select();
                              }
                            }, 50);
                          }
                        }}
                        placeholder="Search item..."
                        isClearable
                        menuPosition="fixed"
                      />
                    </div>
                    
                    <div className="w-32">
                      <label className="text-xs text-gray-500 font-medium mb-1 block">Purchase Unit</label>
                      <select
                        ref={(el) => (unitRefs.current[index] = el)}
                        value={item.purchase_unit_id || ""}
                        onChange={(e) => {
                          updateItem(index, 'purchase_unit_id', e.target.value);
                          setTimeout(() => {
                            quantityRefs.current[index]?.focus();
                            quantityRefs.current[index]?.select();
                          }, 50);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            quantityRefs.current[index]?.focus();
                            quantityRefs.current[index]?.select();
                          }
                        }}
                        className="select select-sm select-bordered w-full"
                      >
                        <option value="">Base ({invUnit})</option>
                        {unitConversions.filter(c => String(c.inventory_item_id) === String(item.inventory_item_id)).map(c => (
                          <option key={c.purchase_unit_id} value={c.purchase_unit_id}>{c.purchase_unit_name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="w-24">
                      <label className="text-xs text-gray-500 font-medium mb-1 block">Quantity</label>
                      <input
                        ref={(el) => (quantityRefs.current[index] = el)}
                        type="number"
                        step="any"
                        placeholder="0"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            totalPriceRefs.current[index]?.focus();
                            totalPriceRefs.current[index]?.select();
                          }
                        }}
                        className="input input-sm input-bordered w-full text-right"
                      />
                    </div>

                    <div className="w-28">
                      <label className="text-xs text-gray-500 font-medium mb-1 block">Total Price</label>
                      <input
                        ref={(el) => (totalPriceRefs.current[index] = el)}
                        type="number"
                        step="any"
                        placeholder="0.00"
                        value={item.total_price}
                        onChange={(e) => updateItem(index, 'total_price', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (e.shiftKey) {
                              discountRef.current?.focus();
                              discountRef.current?.select();
                            } else if (item.inventory_item_id) {
                              if (index === items.length - 1) {
                                addItemRow();
                              } else {
                                itemSelectRefs.current[index + 1]?.focus();
                              }
                            } else {
                              discountRef.current?.focus();
                              discountRef.current?.select();
                            }
                          }
                        }}
                        className="input input-sm input-bordered w-full text-right"
                      />
                    </div>

                    <button onClick={() => removeItemRow(index)} className="btn btn-sm btn-error btn-square text-white ml-2"><IconTrash size={16} /></button>
                  </div>
                  
                  {/* Real-time Conversion Preview */}
                  {item.inventory_item_id && qty > 0 && total > 0 && (
                    <div className="flex items-center gap-2 mt-1 text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                      <IconArrowRight size={14} /> 
                      Converted: {totalBaseUnits} {invUnit} added to stock @ {currency}{pricePerBaseUnit.toFixed(4)} / {invUnit}
                    </div>
                  )}
                </div>
              )})}
              <button onClick={addItemRow} className="btn btn-sm btn-outline btn-block mt-2"><IconPlus size={16} /> Add Item</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm text-gray-500 mb-1 flex items-center gap-1"><IconCreditCard size={14} /> Discount Amount</label>
                  <input
                    ref={discountRef}
                    type="number"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(parseFloat(String(e.target.value).replace(/,/g, "")) || 0)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        paidAmountRef.current?.focus();
                        paidAmountRef.current?.select();
                      }
                    }}
                    className="input input-bordered w-full"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-gray-500 mb-1 flex items-center gap-1"><IconCreditCard size={14} /> Paid Amount</label>
                  <input
                    ref={paidAmountRef}
                    type="number"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(parseFloat(String(e.target.value).replace(/,/g, "")) || 0)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        paymentMethodRef.current?.focus();
                      }
                    }}
                    className="input input-bordered w-full"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Payment Method</label>
                <select
                  ref={paymentMethodRef}
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      notesRef.current?.focus();
                    }
                  }}
                  className="select select-bordered w-full"
                >
                  <option value="Cash">Cash</option>
                  {paymentTypes?.map(pt => (
                    <option key={pt.id} value={pt.title}>{pt.title}</option>
                  ))}
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Check">Check</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Notes</label>
                <textarea
                  ref={notesRef}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitBtnRef.current?.focus();
                    }
                  }}
                  className="textarea textarea-bordered w-full h-20"
                  placeholder="Internal notes..."
                ></textarea>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-black p-4 rounded-xl flex flex-col justify-center">
              <div className="flex justify-between text-lg">
                <p>Subtotal:</p>
                <p>{calculateSubtotal().toFixed(2)}</p>
              </div>
              <div className="flex justify-between text-md text-blue-500 mt-2">
                <p>Discount:</p>
                <p>-{discountAmount ? parseFloat(String(discountAmount).replace(/,/g, "")).toFixed(2) : "0.00"}</p>
              </div>
              <div className="flex justify-between text-2xl font-bold mt-4 text-restro-green border-t pt-4">
                <p>Net Total:</p>
                <p>{calculateTotal().toFixed(2)}</p>
              </div>
              <div className="flex justify-between text-md text-red-500 mt-2">
                <p>Due Amount:</p>
                <p>{Math.max(0, calculateTotal() - (parseFloat(String(paidAmount).replace(/,/g, "")) || 0)).toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="modal-action">
            <form method="dialog" className="flex w-full gap-2">
              <button className="btn flex-1">Cancel</button>
              <button
                ref={submitBtnRef}
                type="button"
                onClick={btnCreatePurchase}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    btnCreatePurchase();
                  }
                }}
                className="btn btn-primary flex-[2]"
              >
                Complete Purchase
              </button>
            </form>
          </div>
        </div>
      </dialog>

      {/* filter dialog */}
      <dialog id="filter-dialog" className="modal">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <IconFilter stroke={iconStroke} /> {t('invoices.filter')}
          </h3>
          {/* filters */}
          <div className="my-4">
            <div>
              <label className=" block text-gray-500 text-sm">{t('invoices.filter')}</label>
              <select
                className="text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green"
                ref={filterTypeRef}
                defaultValue={filter}
              >
                {filters.map((f, index) => (
                  <option key={index} value={f.key}>
                    {f.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 mt-4">
              <div className="flex-1">
                <label
                  htmlFor="fromDate"
                  className=" block text-gray-500 text-sm"
                >
                  {t('invoices.from')}
                </label>
                <input
                  defaultValue={fromDate}
                  type="date"
                  ref={fromDateRef}
                  className="text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green"
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="toDate"
                  className=" block text-gray-500 text-sm"
                >
                  {t('invoices.to')}
                </label>
                <input
                  defaultValue={toDate}
                  type="date"
                  ref={toDateRef}
                  className="text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green"
                />
              </div>
            </div>
          </div>
          {/* filters */}
          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="btn transition-colors px-4 py-3 flex-1 items-center justify-center align-center rounded-xl border border-restro-border-green bg-restro-card-bg hover:bg-restro-button-hover text-restro-text">{t('invoices.close')}</button>
              <button onClick={()=>{
                setFilter(filterTypeRef.current.value);
                setFromDate(fromDateRef.current.value || null);
                setToDate(toDateRef.current.value || null);
                setCurrentPage(1);
              }}  className="rounded-xl transition-colors px-4 py-3 text-white ml-3 border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover">{t('invoices.apply')}</button>
            </form>
          </div>
        </div>
      </dialog>
      {/* filter dialog */}
    </Page>
  );
}
